import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NzTableFilterValue, NzTableSortOrder } from 'ng-zorro-antd/table/src/table.types';
import { BehaviorSubject, lastValueFrom, Observable, Subscription } from 'rxjs';
import { ORDERS } from 'src/app/core/constants/storage';
import { LocalStorageService } from 'src/app/core/services/storage.service';
import { ProductionOrderResponseDto } from 'src/app/shared/dto/pagination/production-order-response.dto';
import { PriorityDto } from 'src/app/shared/dto/priority.dto';
import { ProductionOrderDto } from 'src/app/shared/dto/production-order.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, formatClientRoomId, RoomNames } from 'src/app/shared/sockets/enums/rooms';

@Injectable({ providedIn: 'root', })
export class ProductionOrderService extends BaseService {

    public orders$: Observable<OrderResponse>;
    public productionOrder$: Observable<ProductionOrderDto>;

    private readonly ordersSubject = new BehaviorSubject<OrderResponse>(new OrderResponse());
    private messageSubscription: Subscription | undefined;

    private readonly productionOrderSubject = new BehaviorSubject<ProductionOrderDto>(new ProductionOrderDto());
    private messageProductionOrderSubscription: Subscription | undefined;

    private pageNumber: number = 1;
    private pageSize: number = 20;
    private orderId: number = 0;
    private filterText: string = '';
    private filterMultipleProcessesRunning = false;
    private filterMachineId: number | null = null;
    private filter: Array<{ key: string; value: NzTableFilterValue; }> = [];
    private sort: Array<{ key: string; value: NzTableSortOrder; }> = [];

    private readonly socketService = inject(SocketService);
    private readonly localStorageService = inject(LocalStorageService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'production-orders');

        this.orders$ = this.ordersSubject.asObservable();
        this.productionOrder$ = this.productionOrderSubject.asObservable();
    }

    refreshVariables(pageSize: number, pageNumber: number, filterText:string, filterMultipleProcessesRunning: boolean, filterMachineId: number | null, filter: Array<{ key: string; value: NzTableFilterValue; }>,
        sort: Array<{ key: string; value: NzTableSortOrder; }>): void {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;
        this.filterText = filterText;
        this.filterMultipleProcessesRunning = filterMultipleProcessesRunning;
        this.filterMachineId = filterMachineId;
        this.filter = filter;
        this.sort = sort;
    }

    /**
     * Emite los datos de órdenes al subject para notificar a los suscriptores.
     * Usado cuando se cargan datos desde el componente para mantener sincronizado el estado.
     */
    emitOrders(orders: ProductionOrderDto[], totalOrders: number): void {
        this.ordersSubject.next({ orders, totalOrders });
    }

    async setupWebSocket(pageSize: number, pageNumber: number, orderId?: number): Promise<void> {
        try {
            this.pageSize = pageSize;
            this.pageNumber = pageNumber;

            if (orderId && orderId > 0) {
                const ordersResponse = await this.getOrderById(orderId);
                this.ordersSubject.next({ orders: [ordersResponse], totalOrders: 1 });

                this.orderId = orderId;
            } else {
                const ordersResponse = await this.getPaginatedOrders(this.pageSize, this.pageNumber, this.filterText, this.filterMultipleProcessesRunning, this.filterMachineId, this.filter, this.sort);
                this.ordersSubject.next({ orders: ordersResponse.productionOrders, totalOrders: ordersResponse.total });
            }

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PRODUCTION_ORDERS_ROOM));
            this.messageSubscription = this.socketService.getNotifications(Messages.PRODUCTION_ORDERS_CHANGED)
                .subscribe(async () => {
                    if (orderId && orderId > 0) {
                        const ordersResponse = await this.getOrderById(orderId);
                        this.ordersSubject.next({ orders: [ordersResponse], totalOrders: 1 });

                        this.orderId = orderId;
                    } else {
                        const ordersResponse = await this.getPaginatedOrders(this.pageSize, this.pageNumber, this.filterText, this.filterMultipleProcessesRunning, this.filterMachineId, this.filter, this.sort);
                        this.ordersSubject.next({ orders: ordersResponse.productionOrders, totalOrders: ordersResponse.total });
                    }
                });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Órdenes de Producción. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        if(this.messageSubscription){
            this.messageSubscription.unsubscribe();
        }
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PRODUCTION_ORDERS_ROOM));
        this.orderId = 0;
    }

    /**
     * Configura el WebSocket sin hacer llamada HTTP inicial.
     * La primera carga de datos se hace a través de getPaginatedOrders() llamada desde el componente.
     * Esto evita la doble llamada y permite que los filtros estén correctamente inicializados.
     */
    setupWebSocketOnly(): void {
        try {
            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PRODUCTION_ORDERS_ROOM));
            this.messageSubscription = this.socketService.getNotifications(Messages.PRODUCTION_ORDERS_CHANGED)
                .subscribe(async () => {
                    const ordersResponse = await this.getPaginatedOrders(
                        this.pageSize,
                        this.pageNumber,
                        this.filterText,
                        this.filterMultipleProcessesRunning,
                        this.filterMachineId,
                        this.filter,
                        this.sort
                    );
                    this.ordersSubject.next({ orders: ordersResponse.productionOrders, totalOrders: ordersResponse.total });
                });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Órdenes de Producción. Error: ${e.message}`);
        }
    }

    async setupWebSocketById(orderId: number): Promise<void> {
        try {
            const order = await this.getOrderById(orderId);
            this.productionOrderSubject.next(order);

            this.socketService.conectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.PRODUCTION_ORDERS_ROOM, orderId));
            this.messageProductionOrderSubscription = this.socketService.getNotifications(Messages.PRODUCTION_ORDERS_CHANGED)
                .subscribe(async () => {
                    const order = await this.getOrderById(orderId);
                    this.productionOrderSubject.next(order);
                });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de la Orden de Producción ${orderId}. Error: ${e.message}`);
        }
    }

    disconnectWebSocketById(orderId: number): void {
        if(this.messageProductionOrderSubscription){
            this.messageProductionOrderSubscription.unsubscribe();
        }
        this.socketService.disconnectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.PRODUCTION_ORDERS_ROOM, orderId));
    }

    async getOrderById(id: number): Promise<ProductionOrderDto> {
        try {
            if (id <= 0) {
                throw new Error('El id de la orden ha de ser mayor de 0');
            }

            const request = this.httpClient.get<ProductionOrderDto>(`${this.url}/${id}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener la orden ${id ?? ''}. Error: ${e.message}`);
        }
    }

    async getPaginatedOrders(pageSize: number, pageNumber: number, filterText:string, filterMultipleProcessesRunning: boolean, filterMachineId: number | null, filter: Array<{ key: string; value: NzTableFilterValue; }>,
        sort?: Array<{ key: string; value: NzTableSortOrder; }>): Promise<ProductionOrderResponseDto> {
        try {
            let url: string = `${this.url}/findAll?pageSize=${pageSize}&pageNumber=${pageNumber}`;

            if (sort) {
                if (sort.find(sort => sort.key === 'priorityOrder')?.value) {
                    const sortMode = sort.find(sort => sort.key === 'priorityOrder')?.value;
                    url = `${url}&orderBy=priorityOrder`;

                    if (sortMode) {
                        url = `${url}&sortMode=${sortMode === 'ascend' ? 'asc' : 'desc'}`;
                    }
                }

                if (sort.find(sort => sort.key === 'priority')?.value) {
                    const sortMode = sort.find(sort => sort.key === 'priority')?.value;
                    url = `${url}&orderBy=priority`;

                    if (sortMode) {
                        url = `${url}&sortMode=${sortMode === 'ascend' ? 'asc' : 'desc'}`;
                    }
                }
            }

            if (filter.find(filter => filter.key === 'filterStatus')) {
                url = `${url}&filterStatus=${filter.find(filter => filter.key === 'filterStatus')?.value?.toString()}`;
            }

            if (filterMultipleProcessesRunning) {
                url = `${url}&filterMultipleProcessesRunning=${filterMultipleProcessesRunning}`;
            }

            if (filterMachineId && filterMachineId > 0) {
                url = `${url}&filterMachineId=${filterMachineId}`;
            }

            if (filterText && filterText !== '') {
                url = `${url}&filterText=${filterText}`;
            }

            const request = this.httpClient.get<ProductionOrderResponseDto>(url);

            const ordersResponse: ProductionOrderResponseDto = await lastValueFrom(request);

            return ordersResponse;
        } catch (e: any) {
            throw new Error(`No se ha encontrado ninguna orden de producción. Error: ${e.message}`);
        }
    }

    async createOrder(order: ProductionOrderDto): Promise<ProductionOrderDto> {
        try {
            if (order === null || order === undefined) {
                throw new Error('La orden no es válida');
            }

            const createdOrder: ProductionOrderDto = await lastValueFrom(this.httpClient.post<ProductionOrderDto>(`${this.url}/create`, order));

            if (!createdOrder) {
                throw new Error('Error creando la orden de producción');
            }

            return createdOrder;
        } catch (e: any) {
            throw new Error(`Error creando la orden de producción. Error: ${e.message}`);
        }
    }

    async updateOrder(order: ProductionOrderDto): Promise<ProductionOrderDto> {
        try {
            if (order === null || order === undefined) {
                throw new Error('La orden no es válida');
            }

            const updatedOrder: ProductionOrderDto = await lastValueFrom(this.httpClient.patch<ProductionOrderDto>(`${this.url}/update`, order));

            if (!updatedOrder) {
                throw new Error('Error actualizando la orden de producción');
            }

            return updatedOrder;
        } catch (e: any) {
            throw new Error(`Error actualizando la orden de producción. Error: ${e.message}`);
        }
    }

    async updateOrderStatus(orderId: number, statusId: number): Promise<ProductionOrderDto> {
        try {
            if (orderId <= 0 || statusId <= 0) {
                throw new Error('El id de la orden y del estado han de ser mayor de 0');
            }

            const updatedOrder: ProductionOrderDto = await lastValueFrom(this.httpClient.patch<ProductionOrderDto>(`${this.url}/updateOrderStatus/${orderId}/${statusId}`, {}));

            if (!updatedOrder) {
                throw new Error('Error actualizando el estado de la orden de producción');
            }

            return updatedOrder;
        } catch (e: any) {
            throw new Error(`Error actualizando el estado de la orden de producción. Error: ${e.message}`);
        }
    }

    async deleteOrder(orderId: number): Promise<ProductionOrderDto> {
        try {
            if (orderId <= 0) {
                throw new Error('El id de la orden ha de ser mayor de 0');
            }

            const ordersResponse: ProductionOrderDto = await lastValueFrom(this.httpClient.delete<ProductionOrderDto>(`${this.url}/delete/${orderId}`));

            if (!ordersResponse) {
                throw Error(`No se ha podido eliminar la orden ${orderId}`);
            }

            return ordersResponse;
        } catch (e: any) {
            throw new Error(`Error eliminando la orden de producción ${orderId ?? ''}. Error: ${e.message}`);
        }
    }

    async setAsUrgent(orderId: number): Promise<ProductionOrderDto> {
        try {
            if (!orderId || orderId <= 0) {
                throw new Error('El id de la orden no es válido');
            }

            const updatedOrder: ProductionOrderDto = await lastValueFrom(this.httpClient.patch<any>(`${this.url}/setPriorityAsUrgent/${orderId}`, {}));

            return updatedOrder;
        } catch (e: any) {
            throw new Error(`No se ha podido marcar la orden como urgente. Error: ${e.message}`);
        }
    }

    async getPriorities(): Promise<PriorityDto[]> {
        try {
            return await lastValueFrom(this.httpClient.get<PriorityDto[]>(`${this.url}/getPriorities`));
        } catch (e: any) {
            throw new Error(`No se han podido obtener las prioridades. Error: ${e.message}`);
        }
    }

    async updateOrderPriority(orderId: number, priority: PriorityDto): Promise<ProductionOrderDto> {
        try {
            if (!orderId || orderId <= 0) {
                throw new Error('El id de la orden no es válido');
            }

            if (priority === null || priority === undefined) {
                throw new Error('Prioridad no válida');
            }

            const request = this.httpClient.patch<ProductionOrderDto>(`${this.url}/updateOrderPriority/${orderId}`, priority);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido actualizar la prioridad para la orden ${orderId ?? ''}. Error: ${e.message}`);
        }
    }

    getOrderFromLocalStorage(): ProductionOrderDto | null {
        const orderStr = this.localStorageService.getItem(ORDERS);

        return orderStr as ProductionOrderDto ?? null;
    }
}

export class OrderResponse {
    orders!: ProductionOrderDto[];
    totalOrders!: number;
}
